import { BusinessError, SC_TECHNICAL_FAILURE } from "./service-provider-model.js";
//#region src/service-provider/function-utils.ts
/**
* Utility functions for validating and extracting function call results.
*
* These helpers bridge the gap between the generic FunctionProvider
* (which just returns { statusCode, resultData }) and the application's
* FunctionDefinition (which specifies successCodes).
*/
/**
* Validates a function result against the expected success codes and extracts the data.
*
* @param result - The raw function result from the provider
* @param functionDef - The function definition with successCodes
* @returns The extracted resultData, typed as T
* @throws BusinessError if statusCode is not in successCodes
*
* @example
* ```typescript
* public loadGalleryList(): Observable<DJGalleryItem[]> {
*   const call = { object: "galleryList" };
*   return from(
*     services.functions.callFunction(handlePutFunc.name, call)
*       .then((result) => validateFunctionResult<DJGalleryItem[]>(result, handlePutFunc))
*   );
* }
* ```
*/
function validateFunctionResult(result, functionDef) {
	if (!functionDef.successCodes.includes(result.statusCode)) {
		const message = typeof result.resultData === "string" ? result.resultData : `Function call failed with code ${result.statusCode}`;
		throw new BusinessError(message, result.statusCode);
	}
	return result.resultData;
}
/**
* Validates a function result with a custom success code array.
* Use this when you don't have a full FunctionDefinition available.
*
* @param result - The raw function result from the provider
* @param successCodes - Array of valid status codes (default: [200])
* @returns The extracted resultData, typed as T
* @throws BusinessError if statusCode is not in successCodes
*/
function validateResult(result, successCodes = [200]) {
	if (!successCodes.includes(result.statusCode)) {
		const message = typeof result.resultData === "string" ? result.resultData : `Function call failed with code ${result.statusCode}`;
		throw new BusinessError(message, result.statusCode);
	}
	return result.resultData;
}
/**
* Type guard to check if an error is a BusinessError.
* Useful in catch blocks to distinguish business errors from technical errors.
*
* @example
* ```typescript
* catchError((err) => {
*   if (isBusinessError(err)) {
*     console.log("Business error:", err.code, err.message);
*   }
*   return of(failAction(err));
* })
* ```
*/
function isBusinessError(error) {
	return error instanceof BusinessError;
}
/**
* Extracts error information for logging/display.
* Handles both BusinessError and standard Error.
*/
function extractErrorInfo(error) {
	if (isBusinessError(error)) return {
		message: error.message,
		code: error.code
	};
	if (error instanceof Error) return {
		message: error.message,
		code: SC_TECHNICAL_FAILURE
	};
	return {
		message: String(error),
		code: SC_TECHNICAL_FAILURE
	};
}
//#endregion
export { extractErrorInfo, isBusinessError, validateFunctionResult, validateResult };
